import React from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDoorOpen,
  faClockRotateLeft,
  faHeartPulse,
  faGaugeHigh,
  faFaceSmile,
  faSeedling,
  faStethoscope,
  faHandHoldingHeart,
  faFlaskVial,
  faHeart,
  faWandMagicSparkles,
  faChevronRight,
} from "@fortawesome/free-solid-svg-icons";

import AnvayaHeaderBanner from "./AnvayaHeaderBanner";

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const EHSAAS_ITEMS = [
  {
    label: "PurpleDoor",
    desc: "Daily journal",
    icon: faDoorOpen,
    route: "/purpledoordashboard",
  },
  {
    label: "LifeReplay",
    desc: "Cherished moments",
    icon: faClockRotateLeft,
    route: "/lifereplaydashboard",
  },
  {
    label: "Pulse",
    desc: "Vitals check-in",
    icon: faHeartPulse,
    route: "/pulsedashboard",
  },
  {
    label: "ScorePlus",
    desc: "Wellness score",
    icon: faGaugeHigh,
    route: "/scoreplus",
  },
  {
    label: "Mood Ratings",
    desc: "How you feel",
    icon: faFaceSmile,
    route: "/fanratings",
  },
];

const GROWTH_ITEMS = [
  {
    label: "PurpleDoor",
    desc: "Daily journal",
    icon: faDoorOpen,
    route: "/purpledoordashboard",
  },
  {
    label: "LifeReplay",
    desc: "Milestone memories",
    icon: faClockRotateLeft,
    route: "/lifereplaydashboard",
  },
  {
    label: "Pulse",
    desc: "Vitals check-in",
    icon: faHeartPulse,
    route: "/pulsedashboard",
  },
  {
    label: "ScorePlus",
    desc: "Wellness score",
    icon: faGaugeHigh,
    route: "/scoreplus",
  },
  {
    label: "Mom & Kid Ratings",
    desc: "Shared moods",
    icon: faHeart,
    route: "/fanratings",
  },
  {
    label: "GrowthLoop",
    desc: "Growth milestones",
    icon: faSeedling,
    route: "/growthloop",
  },
];

const CARE_NETWORK = [
  {
    role: "Doctor",
    icon: faStethoscope,
    desc: "A clinical view of every expecting mother in your care.",
    chips: [
      { label: "Expecting Mothers", route: "/scoreplus-doctor" },
      { label: "Child", route: "/growthloop" },
    ],
  },
  {
    role: "Health Advisor",
    icon: faHandHoldingHeart,
    desc: "Track and support the expecting mothers in your community.",
    chips: [
      { label: "Expecting Mothers", route: "/scoreplus-health-advisor" },
      { label: "Child", route: "/growthloop" },
    ],
  },
  {
    role: "Pharma Partner",
    icon: faFlaskVial,
    desc: "Aggregate insight across the maternity and early-childhood journey.",
    chips: [
      { label: "Maternity Audience", route: "/scoreplus-pharma" },
      { label: "Early Childhood", route: "/growthloop" },
    ],
  },
];

/* ------------------------------------------------------------------ */
/*  Small building blocks                                              */
/* ------------------------------------------------------------------ */

function SectionHeading({ eyebrow, title, subtitle, emoji, tone }) {
  const toneText =
    tone === "coral"
      ? "text-[#C07A9A]"
      : tone === "amber"
        ? "text-[#C49A6C]"
        : "text-[#8B6BB8]";
  return (
    <div className="mb-4 sm:mb-5">
      <span
        className={`font-mono text-[0.65rem] tracking-[0.22em] uppercase ${toneText} opacity-100`}
      >
        {eyebrow}
      </span>
      <h2 className="mt-1 flex items-center gap-2 font-display text-[1.35rem] sm:text-[1.6rem] font-semibold text-[#3D2168] leading-tight">
        <span aria-hidden="true">{emoji}</span>
        {title}
      </h2>
      <p className="mt-1 text-[0.8rem] sm:text-[0.86rem] text-[#6E5585]">
        {subtitle}
      </p>
    </div>
  );
}

function NavTile({ item, tone, onNavigate }) {
  const toneStyles = {
    coral: {
      ring: "border-[#E8A0B8]/40 hover:border-[#E07A9A]/70",
      bg: "bg-white/70 hover:bg-[#FCE8EF]",
      glow: "hover:shadow-[0_10px_28px_-8px_rgba(224,122,154,0.28)]",
      iconWrap: "bg-[#FCE8EF] text-[#C07A9A]",
    },
    amber: {
      ring: "border-[#E8C9A0]/45 hover:border-[#D4A86A]/70",
      bg: "bg-white/70 hover:bg-[#FFF5E6]",
      glow: "hover:shadow-[0_10px_28px_-8px_rgba(212,168,106,0.28)]",
      iconWrap: "bg-[#FFF5E6] text-[#C49A6C]",
    },
    lavender: {
      ring: "border-[#D4C0F0]/45 hover:border-[#B88BC8]/70",
      bg: "bg-white/70 hover:bg-[#F3E8FF]",
      glow: "hover:shadow-[0_10px_28px_-8px_rgba(184,139,200,0.28)]",
      iconWrap: "bg-[#F3E8FF] text-[#8B6BB8]",
    },
  }[tone];

  return (
    <button
      type="button"
      onClick={() => onNavigate(item.route)}
      className={`group relative flex flex-col items-start gap-2.5 rounded-2xl border ${toneStyles.ring}
        ${toneStyles.bg} backdrop-blur-md px-3.5 py-3.5 text-left
        shadow-[0_4px_18px_-6px_rgba(180,140,160,0.12)]
        transition-all duration-200 ease-out
        active:scale-[0.97] motion-safe:hover:-translate-y-0.5
        ${toneStyles.glow}
        focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E07A9A]
        min-h-[92px] w-full`}
    >
      <span
        className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ${toneStyles.iconWrap}`}
      >
        <FontAwesomeIcon
          icon={item.icon}
          className="w-[18px] h-[18px]"
          aria-hidden="true"
        />
      </span>
      <span className="min-w-0">
        <span className="block text-[0.8rem] font-semibold text-[#3D2168] leading-tight">
          {item.label}
        </span>
        <span className="block text-[0.68rem] text-[#6E5585] leading-snug mt-0.5">
          {item.desc}
        </span>
      </span>
      <FontAwesomeIcon
        icon={faChevronRight}
        className="absolute right-3 top-3.5 w-[12px] h-[12px] text-[#B8A0C8]/60 group-hover:text-[#8B6BB8] transition-colors"
        aria-hidden="true"
      />
    </button>
  );
}

function NavGrid({ items, tone, onNavigate }) {
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2.5 sm:gap-3">
      {items.map((item) => (
        <NavTile
          key={item.label + item.route}
          item={item}
          tone={tone}
          onNavigate={onNavigate}
        />
      ))}
    </div>
  );
}

function CareCard({ entry, onNavigate }) {
  return (
    <div className="rounded-2xl border border-[#D4C0F0]/40 bg-white/70 backdrop-blur-md px-4 py-4 sm:px-5 sm:py-5 flex flex-col gap-3 shadow-[0_4px_18px_-6px_rgba(180,140,160,0.12)]">
      <div className="flex items-center gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#F3E8FF] text-[#8B6BB8]">
          <FontAwesomeIcon
            icon={entry.icon}
            className="w-[19px] h-[19px]"
            aria-hidden="true"
          />
        </span>
        <h3 className="font-display text-[1rem] font-semibold text-[#3D2168]">
          {entry.role}
        </h3>
      </div>
      <p className="text-[0.78rem] text-[#6E5585] leading-relaxed">
        {entry.desc}
      </p>
      <div className="flex flex-wrap gap-2 mt-1">
        {entry.chips.map((chip) => (
          <button
            key={chip.label + chip.route}
            type="button"
            onClick={() => onNavigate(chip.route)}
            className="inline-flex items-center gap-1 rounded-full border border-[#D4C0F0]/50
              bg-[#F3E8FF]/80 px-3 py-1.5 text-[0.7rem] font-medium text-[#6E5585]
              hover:bg-[#EDE0F5] active:scale-[0.96] transition-all
              focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#B88BC8]"
          >
            {chip.label}
            <FontAwesomeIcon
              icon={faChevronRight}
              className="w-[10px] h-[10px]"
              aria-hidden="true"
            />
          </button>
        ))}
      </div>
    </div>
  );
}

/* Thread rail — the "Anvaya" signature: a connecting sequence that links
   every stage of the journey, since Anvaya itself means "sequence /
   connection". Each stage gets a node on the thread. */
function ThreadNode({ emoji, tone }) {
  const dot =
    tone === "coral"
      ? "bg-[#E8A0B8]"
      : tone === "amber"
        ? "bg-[#E8C9A0]"
        : "bg-[#D4C0F0]";
  return (
    <div className="relative flex sm:flex-col items-center sm:h-full">
      <span
        className={`flex h-8 w-8 sm:h-9 sm:w-9 items-center justify-center rounded-full ${dot}
        text-[0.9rem] shadow-[0_0_16px_rgba(224,160,180,0.25)] shrink-0`}
        aria-hidden="true"
      >
        {emoji}
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Main                                                                */
/* ------------------------------------------------------------------ */

export default function Anvaya({ navigate }) {
  // Falls back to a hard redirect if no router `navigate` fn is supplied,
  // but pass your own (react-router's useNavigate(), Next's router.push, etc.)
  const go = (route) => {
    if (navigate) navigate(route);
    else if (typeof window !== "undefined") window.location.href = route;
  };

  return (
    <div className="relative w-full min-h-[100dvh] overflow-x-hidden bg-gradient-to-b from-[#FCE8EF] via-[#F8EDD9] to-[#F3E8FF] font-sans text-[#3D2168]">
      {/* Only what Tailwind genuinely can't express: one display font and a
          scrollbar-hide rule for the mobile swipe row. Everything else below
          is Tailwind utility classes. */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600;9..144,700&display=swap');
        .font-display { font-family: 'Fraunces', Georgia, serif; }
      `}</style>

      {/* Soft ambient background — warm, luminous, nurturing */}
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_120%_60%_at_50%_0%,#FFF5F8_0%,#FCE8EF_40%,#F3E8FF_100%)]" />
        <div className="absolute -top-24 -left-16 h-72 w-72 rounded-full bg-[#F8C1D0]/25 blur-[110px]" />
        <div className="absolute top-[30%] -right-20 h-80 w-80 rounded-full bg-[#F5D5A8]/20 blur-[120px]" />
        <div className="absolute bottom-0 left-1/3 h-72 w-72 rounded-full bg-[#E8C9F0]/25 blur-[120px]" />
      </div>

      <AnvayaHeaderBanner />

      {/* Journey — the connecting thread */}
      <main className="relative z-10 px-4 sm:px-6 pb-10 max-w-3xl mx-auto">
        {/* Ehsaas */}
        <section className="flex gap-3 sm:gap-4">
          <div className="flex flex-col items-center pt-1">
            <ThreadNode emoji="🌸" tone="coral" />
            <span className="w-px flex-1 mt-1 bg-gradient-to-b from-[#E8A0B8]/50 to-[#E8C9A0]/50" />
          </div>
          <div className="flex-1 pb-8">
            <SectionHeading
              eyebrow="Anvaya Ehsaas · Daily"
              title="For the Mom-to-Be"
              subtitle="A gentle daily check-in that holds her feelings, her body, and her story."
              emoji="🌸"
              tone="coral"
            />
            <NavGrid items={EHSAAS_ITEMS} tone="coral" onNavigate={go} />
          </div>
        </section>

        {/* Growth */}
        <section className="flex gap-3 sm:gap-4">
          <div className="flex flex-col items-center pt-1">
            <ThreadNode emoji="🌿" tone="amber" />
            <span className="w-px flex-1 mt-1 bg-gradient-to-b from-[#E8C9A0]/50 to-[#D4C0F0]/50" />
          </div>
          <div className="flex-1 pb-8">
            <SectionHeading
              eyebrow="Anvaya Growth · Daily"
              title="For Mom & Kid"
              subtitle="One thread that grows with her — through pregnancy and into every childhood milestone."
              emoji="🌿"
              tone="amber"
            />
            <NavGrid items={GROWTH_ITEMS} tone="amber" onNavigate={go} />
          </div>
        </section>

        {/* Care network */}
        <section className="flex gap-3 sm:gap-4">
          <div className="flex flex-col items-center pt-1">
            <ThreadNode emoji="🤝" tone="lavender" />
          </div>
          <div className="flex-1 pb-4">
            <SectionHeading
              eyebrow="Anvaya Care Network"
              title="Everyone Around Her"
              subtitle="The doctors, community advisors / workers, and partners who walk this journey alongside her."
              emoji="🤝"
              tone="lavender"
            />
            <div className="flex flex-col gap-3 sm:grid sm:grid-cols-3">
              {CARE_NETWORK.map((entry) => (
                <CareCard key={entry.role} entry={entry} onNavigate={go} />
              ))}
            </div>
          </div>
        </section>

        {/* Closing encouragement */}
        <div className="mt-2 flex items-start gap-2.5 px-4 py-3.5 sm:px-5 sm:py-4 rounded-2xl border border-[#E8A0B8]/35 bg-white/70 backdrop-blur-md shadow-[0_4px_18px_-6px_rgba(180,140,160,0.12)]">
          <FontAwesomeIcon
            icon={faWandMagicSparkles}
            className="w-[16px] h-[16px] text-[#C49A6C] shrink-0 mt-0.5"
            aria-hidden="true"
          />
          <p className="text-[0.85rem] sm:text-[0.88rem] font-medium text-[#3D2168] leading-snug">
            You're doing amazing, Mom.
            <br />
            <span className="text-[0.76rem] font-normal text-[#6E5585]">
              We're so proud of you — and we're keeping every step in Anvaya.
            </span>
          </p>
        </div>

        {/* Local Economy ecosystem links */}
        <div className="mt-6 flex flex-col items-center gap-3">
          <p className="text-[0.65rem] font-mono uppercase tracking-[0.18em] text-[#C07A9A]/80">
            Connected to Pados / Neighbourhood / Local Economy
          </p>

          <div className="flex flex-wrap items-center justify-center gap-2.5">
            {[
              { label: "FindItLocal", emoji: "📍", route: "/finditlocal" },
              { label: "FoundHer", emoji: "🌸", route: "/foundher" },
              { label: "FiLTV", emoji: "📺", route: "/filtv" },
            ].map((item) => (
              <button
                key={item.route}
                type="button"
                onClick={() => go(item.route)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#E8A0B8]/60 
          bg-white/70 w-fit px-3.5 py-1.5 text-[0.72rem] font-medium text-[#3D2168]
          backdrop-blur-sm transition-all duration-200
          hover:scale-[1.03] hover:border-[#E07A9A]/80 hover:bg-[#FCE8EF]/90
          active:scale-[0.97]
          focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#E07A9A]"
              >
                <span className="text-[0.9rem]" aria-hidden="true">
                  {item.emoji}
                </span>
                {item.label}
              </button>
            ))}
          </div>
        </div>

        <p className="mt-6 text-center text-[0.68rem] text-[#8B6BB8]/70 font-mono tracking-wide">
          THINK5WHYS · ANVAYA
        </p>
      </main>
    </div>
  );
}